import React from 'react';
import PositionsList from 'features/docs/components/PositionsList';
import MetaButton from 'shared/ui/MetaButton';
import { Tabs, Tab } from 'shared/ui/Tabs';
import { formatDate } from 'shared/utils/format';

const formatPrice = (price) => Math.round(price || 0).toLocaleString('ru-RU');

// Document style labels
const STYLE_LABELS = {
  changes: 'Изменения',
  sale: 'Продажа',
  purchase: 'Закупка',
  transfer: 'Перемещение',
  return: 'Возврат',
  writeoff: 'Списание',
};

// Sub-style labels
const SUB_STYLE_LABELS = {
  inventory: 'Инвентаризация',
  sale: 'Продажа',
  purchase: 'Закупка',
  transfer_in: 'Приём',
  transfer_out: 'Отправка',
  return_supplier: 'Возврат поставщику',
  return_client: 'Возврат от клиента',
  writeoff: 'Списание',
};

// Style colors
const STYLE_COLORS = {
  changes: 'bg-purple-500',
  sale: 'bg-green-500',
  purchase: 'bg-blue-500',
  transfer: 'bg-yellow-500',
  return: 'bg-orange-500',
  writeoff: 'bg-red-500',
};

const DocItem = ({ item }) => {
  const styleLabel = STYLE_LABELS[item.style] || item.style;
  const subStyleLabel = SUB_STYLE_LABELS[item.sub_style] || item.sub_style;
  const styleColor = STYLE_COLORS[item.style] || 'bg-gray-500';

  const filterString = `doc=${item.id}`;

  return (
    <>
      {/* Header Section */}
      <div className='mb-6'>
        <div className='flex justify-between items-start'>
          <h1 className='text-2xl font-normal mb-1'>{item.meta?.name || `Документ #${item.number}`}</h1>
          <span className='text-gray-400'>Документ</span>
        </div>
        <div className='text-sm mb-2'>
          Создан {formatDate(item.created)}
        </div>
        <div className='flex gap-2 flex-wrap'>
          <span className={`${styleColor} text-white text-sm px-2 py-1 rounded`}>
            {styleLabel}
          </span>
          {item.sub_style && (
            <span className='bg-gray-600 text-white text-sm px-2 py-1 rounded'>
              {subStyleLabel}
            </span>
          )}
          <span className={`${item.status ? 'bg-green-500' : 'bg-red-500'} text-white text-sm px-2 py-1 rounded`}>
            {item.status ? 'Проведён' : 'Не проведён'}
          </span>
          {item.kkm && (
            <span className='bg-blue-500 text-white text-sm px-2 py-1 rounded'>
              ККМ
            </span>
          )}
        </div>
      </div>

      {/* Document Info Section */}
      <div className='mb-6'>
        <h2 className='text-lg font-medium mb-2'>ИНФОРМАЦИЯ</h2>
        <div className='space-y-2 max-w-md'>
          <div className='flex gap-4 border-b border-dotted pb-1'>
            <span className='text-gray-500 w-36 shrink-0'>Номер документа</span>
            <span className='text-blue-600'>{item.number}</span>
          </div>
          {item.cloudshop_id && (
            <div className='flex gap-4 border-b border-dotted pb-1'>
              <span className='text-gray-500 w-36 shrink-0'>CloudShop ID</span>
              <span className='font-mono text-sm'>{item.cloudshop_id}</span>
            </div>
          )}
          <div className='flex gap-4 border-b border-dotted pb-1'>
            <span className='text-gray-500 w-36 shrink-0'>Дата документа</span>
            <span>{formatDate(item.date)}</span>
          </div>
          <div className='flex gap-4 border-b border-dotted pb-1'>
            <span className='text-gray-500 w-36 shrink-0'>Обновлён</span>
            <span>{formatDate(item.updated)}</span>
          </div>
          {item._from && (
            <div className='flex gap-4 border-b border-dotted pb-1'>
              <span className='text-gray-500 w-36 shrink-0'>Откуда</span>
              <span className='text-blue-600'>{MetaButton(item._from)}</span>
            </div>
          )}
          {item._to && (
            <div className='flex gap-4 border-b border-dotted pb-1'>
              <span className='text-gray-500 w-36 shrink-0'>Куда</span>
              <span className='text-blue-600'>{MetaButton(item._to)}</span>
            </div>
          )}
          {item.consultant && (
            <div className='flex gap-4 border-b border-dotted pb-1'>
              <span className='text-gray-500 w-36 shrink-0'>Консультант</span>
              <span>{MetaButton(item.consultant)}</span>
            </div>
          )}
          {item.comment && (
            <div className='flex gap-4 border-b border-dotted pb-1'>
              <span className='text-gray-500 w-36 shrink-0'>Комментарий</span>
              <span className='text-gray-600'>{item.comment}</span>
            </div>
          )}
        </div>
      </div>

      {/* Financial Section */}
      <div className='mb-6'>
        <h2 className='text-lg font-medium mb-4'>ФИНАНСЫ</h2>
        <div className='grid gap-4 grid-cols-2'>
          <div className='space-y-2'>
            <div className='flex justify-between border-b border-dotted pb-1'>
              <span>Количество</span>
              <span className={item.quantity < 0 ? 'text-red-600' : 'text-green-600'}>
                {formatPrice(item.quantity)} шт
              </span>
            </div>
            <div className='flex justify-between border-b border-dotted pb-1'>
              <span>Сумма</span>
              <span className={item.sum < 0 ? 'text-red-600' : 'text-green-600'}>
                {formatPrice(item.sum)} сом
              </span>
            </div>
            <div className='flex justify-between border-b border-dotted pb-1'>
              <span>Подытог</span>
              <span>{formatPrice(item.sub)} сом</span>
            </div>
            <div className='flex justify-between border-b border-dotted pb-1'>
              <span>Закупочная сумма</span>
              <span>{formatPrice(item.purchase)} сом</span>
            </div>
            <div className='flex justify-between border-b border-dotted pb-1'>
              <span>Прибыль</span>
              <span className={item.profit < 0 ? 'text-red-600' : 'text-green-600'}>
                {formatPrice(item.profit)} сом
              </span>
            </div>
          </div>
          <div className='space-y-2'>
            {(item.discount_sum > 0 || item.discount_percent > 0) && (
              <>
                <div className='flex justify-between border-b border-dotted pb-1'>
                  <span>Скидка (сумма)</span>
                  <span className='text-orange-600'>{formatPrice(item.discount_sum)} сом</span>
                </div>
                <div className='flex justify-between border-b border-dotted pb-1'>
                  <span>Скидка (%)</span>
                  <span className='text-orange-600'>{Math.round(item.discount_percent)}%</span>
                </div>
              </>
            )}
            {(item.bonus_cashback > 0 || item.bonus_discount > 0 || item.bonus_spent > 0) && (
              <>
                <div className='flex justify-between border-b border-dotted pb-1'>
                  <span>Бонус кешбек</span>
                  <span className='text-purple-600'>{formatPrice(item.bonus_cashback)}</span>
                </div>
                <div className='flex justify-between border-b border-dotted pb-1'>
                  <span>Бонус скидка</span>
                  <span className='text-purple-600'>{formatPrice(item.bonus_discount)}</span>
                </div>
                <div className='flex justify-between border-b border-dotted pb-1'>
                  <span>Бонус потрачено</span>
                  <span className='text-purple-600'>{formatPrice(item.bonus_spent)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div>
        <Tabs initialTab='positions'>
          <Tab key='positions' label={`Позиции (${item.positions?.meta?.size || 0})`}>
            <PositionsList doc={item} />
          </Tab>
          <Tab key='history' label='История'>
            <div className='text-center text-gray-500 font-medium py-4'>
              История изменений будет добавлена позже
            </div>
          </Tab>
        </Tabs>
      </div>
    </>
  );
};

export default DocItem;